import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import AuthWrapper from "./components/common/AuthWrapper";
import Layout from "./components/layout/Layout";
import Home from "./pages/Home";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Products from "./pages/Products";
import Product from "./pages/Product";
import Categories from "./pages/Categories";
import Category from "./pages/Category";
import Inquiry from "./pages/Inquiry";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Support from "./pages/Support";
import Verify from "./pages/Verify";
import ErrorPage from "./pages/404";

// Blog Pages
import BlogPage from "./pages/Blog";
import BlogPost from "./pages/Blog/BlogPost";
import BlogCategories from "./pages/Blog/BlogCategories";
import BlogTags from "./pages/Blog/BlogTags";
import BlogAuthor from "./pages/Blog/BlogAuthor";

// Admin Pages
import AdminLogin from "./pages/Admin/AdminLogin";
import AdminLayout from "./pages/Admin/AdminLayout";
import AdminDashboard from "./pages/Admin/AdminDashboard";
import AdminsManagement from "./pages/Admin/AdminsManagement";
import RequestsManagement from "./pages/Admin/RequestsManagement";
import MessagesManagement from "./pages/Admin/MessagesManagement";
import NotificationsManagement from "./pages/Admin/NotificationsManagement";
import ProductsManagement from "./pages/Admin/ProductsManagement";
import SettingsManagement from "./pages/Admin/SettingsManagement";
import BlogManagement from "./pages/Admin/BlogManagement";

// User Pages
import UserLogin from "./pages/User/UserLogin";
import UserRegister from "./pages/User/UserRegister";
import ForgotPassword from "./pages/User/ForgotPassword";
import UserLayout from "./pages/User/UserLayout";
import UserProfile from "./pages/User/UserProfile";
import UserRequests from "./pages/User/UserRequests";
import UserNotifications from "./pages/User/UserNotifications";
import UserWishlist from "./pages/User/UserWishlist";
import UserSettings from "./pages/User/UserSettings";

// Protected Routes
import ProtectedRoute from "./components/common/ProtectedRoute";
import UserProtectedRoute from "./components/common/UserProtectedRoute";
import PublicRoute from "./components/common/PublicRoute";

function App() {
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem("darkMode");
    return saved ? JSON.parse(saved) : false;
  });

  useEffect(() => {
    localStorage.setItem("darkMode", JSON.stringify(darkMode));
    if (darkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [darkMode]);

  return (
    <Router>
      <AuthWrapper>
        <div className={darkMode ? "dark" : ""}>
          <AnimatePresence mode="wait">
            <Routes>
              {/* ========== PUBLIC ROUTES ========== */}
              <Route
                path="/"
                element={
                  <Layout darkMode={darkMode} setDarkMode={setDarkMode}>
                    <Home />
                  </Layout>
                }
              />
              <Route
                path="/"
                element={
                  <Layout darkMode={darkMode} setDarkMode={setDarkMode}>
                    <Home />
                  </Layout>
                }
              />
              <Route
                path="/about"
                element={
                  <Layout darkMode={darkMode} setDarkMode={setDarkMode}>
                    <About />
                  </Layout>
                }
              />
              <Route
                path="/blog"
                element={
                  <Layout darkMode={darkMode} setDarkMode={setDarkMode}>
                    <BlogPage />
                  </Layout>
                }
              />
              <Route
                path="/blog/:slug"
                element={
                  <Layout darkMode={darkMode} setDarkMode={setDarkMode}>
                    <BlogPost />
                  </Layout>
                }
              />
              <Route
                path="/blog/category/:slug"
                element={
                  <Layout darkMode={darkMode} setDarkMode={setDarkMode}>
                    <BlogCategories />
                  </Layout>
                }
              />
              <Route
                path="/blog/tag/:tag"
                element={
                  <Layout darkMode={darkMode} setDarkMode={setDarkMode}>
                    <BlogTags />
                  </Layout>
                }
              />
              <Route
                path="/blog/author/:authorId"
                element={
                  <Layout darkMode={darkMode} setDarkMode={setDarkMode}>
                    <BlogAuthor />
                  </Layout>
                }
              />
              <Route
                path="/about"
                element={
                  <Layout darkMode={darkMode} setDarkMode={setDarkMode}>
                    <About />
                  </Layout>
                }
              />
              <Route
                path="/contact"
                element={
                  <Layout darkMode={darkMode} setDarkMode={setDarkMode}>
                    <Contact />
                  </Layout>
                }
              />
              <Route
                path="/products"
                element={
                  <Layout darkMode={darkMode} setDarkMode={setDarkMode}>
                    <Products />
                  </Layout>
                }
              />
              <Route
                path="/products/:slug"
                element={
                  <Layout darkMode={darkMode} setDarkMode={setDarkMode}>
                    <Product />
                  </Layout>
                }
              />
              <Route
                path="/categories"
                element={
                  <Layout darkMode={darkMode} setDarkMode={setDarkMode}>
                    <Categories />
                  </Layout>
                }
              />
              <Route
                path="/categories/:slug"
                element={
                  <Layout darkMode={darkMode} setDarkMode={setDarkMode}>
                    <Category />
                  </Layout>
                }
              />
              <Route
                path="/inquiry"
                element={
                  <UserProtectedRoute>
                    <Layout darkMode={darkMode} setDarkMode={setDarkMode}>
                      <Inquiry />
                    </Layout>
                  </UserProtectedRoute>
                }
              />
              <Route
                path="/privacy"
                element={
                  <Layout darkMode={darkMode} setDarkMode={setDarkMode}>
                    <Privacy />
                  </Layout>
                }
              />
              <Route
                path="/terms"
                element={
                  <Layout darkMode={darkMode} setDarkMode={setDarkMode}>
                    <Terms />
                  </Layout>
                }
              />
              <Route
                path="/support"
                element={
                  <Layout darkMode={darkMode} setDarkMode={setDarkMode}>
                    <Support />
                  </Layout>
                }
              />
              <Route path="/verify-email" element={<Verify />} />

              {/* ========== USER AUTH ROUTES (Public - redirect if logged in) ========== */}
              <Route
                path="/user/login"
                element={
                  <PublicRoute redirectTo="/user">
                    <UserLogin />
                  </PublicRoute>
                }
              />
              <Route
                path="/user/register"
                element={
                  <PublicRoute redirectTo="/user">
                    <UserRegister />
                  </PublicRoute>
                }
              />
              <Route
                path="/user/forgot-password"
                element={
                  <PublicRoute redirectTo="/user">
                    <ForgotPassword />
                  </PublicRoute>
                }
              />

              {/* ========== USER PROTECTED ROUTES ========== */}
              <Route
                path="/user"
                element={
                  <UserProtectedRoute>
                    <UserLayout />
                  </UserProtectedRoute>
                }
              >
                <Route index element={<UserProfile />} />
                <Route path="profile" element={<UserProfile />} />
                <Route path="requests" element={<UserRequests />} />
                <Route path="wishlist" element={<UserWishlist />} />
                <Route path="notifications" element={<UserNotifications />} />
                <Route path="settings" element={<UserSettings />} />
              </Route>

              {/* ========== ADMIN AUTH ROUTE (Public - redirect if logged in) ========== */}
              <Route
                path="/admin/login"
                element={
                  <PublicRoute redirectTo="/admin">
                    <AdminLogin />
                  </PublicRoute>
                }
              />

              {/* ========== ADMIN PROTECTED ROUTES ========== */}
              <Route
                path="/admin"
                element={
                  <ProtectedRoute requireAdmin={true}>
                    <AdminLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<AdminDashboard />} />
                <Route path="admins" element={<AdminsManagement />} />
                <Route path="requests" element={<RequestsManagement />} />
                <Route path="messages" element={<MessagesManagement />} />
                <Route
                  path="notifications"
                  element={<NotificationsManagement />}
                />
                <Route path="blog" element={<BlogManagement />} />
                <Route path="products" element={<ProductsManagement />} />
                <Route path="settings" element={<SettingsManagement />} />
              </Route>

              {/* ========== 404 ROUTE ========== */}
              <Route
                path="*"
                element={
                  <Layout darkMode={darkMode} setDarkMode={setDarkMode}>
                    <ErrorPage />
                  </Layout>
                }
              />
            </Routes>
          </AnimatePresence>
        </div>
      </AuthWrapper>
    </Router>
  );
}

export default App;
